import type { PrimitiveType, PrimitiveTypeByName, PrimitiveTypeDecimalParams, PrimitiveTypeName, PrimitiveTypeStringParams } from '#layers/data-model/types/schema/primitive-type';
import { isDeepEqual } from '#layers/base/public';
import { getPrimitiveFieldPresentationDefaults } from '#layers/data-model/constants/fields';
import { getFieldInputMetaProps } from '#layers/data-model/utils/fields/presentation';

const PARAMS_DEFAULTS = {
	string: { length: 'unlimited' } as const,
	decimal: { precision: 10, scale: 2 } as const,
} satisfies {
	string: PrimitiveTypeStringParams;
	decimal: PrimitiveTypeDecimalParams;
};

type TypesWithParams = keyof typeof PARAMS_DEFAULTS;

type ParamsForType<TName extends PrimitiveTypeName> =
	TName extends 'string' ? PrimitiveTypeStringParams
		: TName extends 'decimal' ? PrimitiveTypeDecimalParams
			: never;

export function createPrimitiveType<TName extends PrimitiveTypeName>(
	name: TName,
	params?: TName extends TypesWithParams ? ParamsForType<TName> : never,
): PrimitiveTypeByName<TName> {
	if (name in PARAMS_DEFAULTS) {
		return {
			name,
			params: params ?? PARAMS_DEFAULTS[name as TypesWithParams],
		} as PrimitiveTypeByName<TName>;
	}

	return { name } as PrimitiveTypeByName<TName>;
}

/**
 * Whether the configured input block (inputId + typeOptions + inputOptions) is identical
 * to the default input block for the given primitive type. When true, the toggle between
 * "configured" and "plain" input can be hidden since they would render the same thing.
 */
export function isConfiguredInputDefault(
	primitiveType: PrimitiveType,
	inputId?: string | null,
	typeOptions?: Record<string, unknown> | null,
	inputOptions?: Record<string, unknown> | null,
): boolean {
	if (!inputId) return true;

	const defaults = getPrimitiveFieldPresentationDefaults(primitiveType).input;

	if (inputId !== defaults.block) return false;

	const configuredProps = getFieldInputMetaProps({ typeOptions, inputOptions });
	const defaultProps = defaults.props ?? {};

	return isDeepEqual(configuredProps, defaultProps);
}
